"use client";

import { useRef } from "react";
import { motion, useReducedMotion } from "framer-motion";
import type { Program } from "@/content/programs/types";
import { HeroBackground } from "@/components/home/hero/HeroBackground";
import { HeroCTAButtons } from "@/components/home/hero/HeroCTAButtons";
import { ProgramStatsBar } from "@/components/programs/ProgramStatsBar";
import { EASE_SMOOTH } from "@/components/motion/constants";
import { TextReveal } from "@/components/motion/TextReveal";
import { bloodHub } from "@/content/blood-hub";

interface ProgramCharityHeroProps {
  program: Program;
  eyebrow: string;
}

export function ProgramCharityHero({ program, eyebrow }: ProgramCharityHeroProps) {
  const reduced = useReducedMotion();
  const backgroundRef = useRef<HTMLDivElement>(null);

  return (
    <section className="relative flex min-h-[min(85vh,720px)] flex-col justify-end overflow-hidden">
      <HeroBackground
        ref={backgroundRef}
        src={program.image}
        alt={program.title}
        kenBurns={!reduced}
        objectPosition="center"
        overlay="soft"
      />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-12 pt-28 md:px-6 md:pb-16">
        <div className="max-w-2xl">
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--charity-gold)] drop-shadow-sm">
            {eyebrow}
          </p>
          <h1 className="font-display text-4xl font-bold leading-tight text-white drop-shadow md:text-5xl lg:text-6xl">
            <TextReveal
              text={program.hero.headline}
              as="span"
              className="inline"
              trigger="mount"
              delay={0.05}
            />{" "}
            <TextReveal
              text={program.hero.accent}
              as="span"
              className="inline text-[var(--charity-gold)]"
              trigger="mount"
              delay={0.22}
            />
          </h1>
          <motion.p
            className="mt-4 max-w-xl text-sm leading-relaxed text-white/90 drop-shadow sm:text-base"
            initial={reduced ? false : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE_SMOOTH, delay: 0.15 }}
          >
            {program.hero.subtext}
          </motion.p>
          <div className="mt-6">
            <HeroCTAButtons
              primary={{ href: "/donate", label: "Donate" }}
              secondary={
                program.slug === bloodHub.programSlug
                  ? {
                      href: bloodHub.playStoreUrl,
                      label: "Download Blood Hub",
                      external: true,
                    }
                  : { href: "/contact", label: "Get involved" }
              }
            />
          </div>
        </div>

        <div className="mt-10 md:mt-12">
          <ProgramStatsBar stats={program.overview.stats} />
        </div>
      </div>
    </section>
  );
}
